import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { BookOpen, Plus, Pencil, Trash2, Copy, Check, BarChart3 } from "lucide-react";
import { usePreLessonStore } from "@/hooks/use-pre-lesson-store";
import ApiClient from "@/lib/api-client";
import { useState } from "react";
import type { PreLessonValues } from "@live-writing/models";

function formatDate(dateString: string) {
  return new Date(dateString).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function PreLessonCard({
  lesson,
  onDelete,
  isDeleting,
}: {
  lesson: PreLessonValues;
  onDelete: (id: number) => void;
  isDeleting: boolean;
}) {
  const [copied, setCopied] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const copyStudentLink = async () => {
    const baseUrl = window.location.origin;
    const studentLink = `${baseUrl}/student/${lesson.code}`;
    await navigator.clipboard.writeText(studentLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDelete = () => {
    if (isDeleting) return;
    if (showDeleteConfirm) {
      onDelete(lesson.id);
    } else {
      setShowDeleteConfirm(true);
      setTimeout(() => setShowDeleteConfirm(false), 3000);
    }
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-5 shadow-sm hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between gap-4 mb-3">
        <div className="flex-1 min-w-0">
          <Link
            to={`/edit-pre-lesson/${lesson.id}`}
            className="text-[16px] font-semibold text-foreground hover:text-primary transition-colors line-clamp-1"
          >
            {lesson.title}
          </Link>
          <p className="text-[13px] text-muted-foreground mt-0.5">
            {lesson.subject}{lesson.topic && ` · ${lesson.topic}`}
          </p>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <Link to={`/teacher/${lesson.code}`}>
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0 text-muted-foreground hover:text-primary"
            >
              <BarChart3 className="h-4 w-4" />
            </Button>
          </Link>
          <Link to={`/edit-pre-lesson/${lesson.id}`}>
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0 text-muted-foreground hover:text-foreground"
            >
              <Pencil className="h-4 w-4" />
            </Button>
          </Link>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleDelete}
            disabled={isDeleting}
            className={`h-8 w-8 p-0 ${showDeleteConfirm ? "text-destructive hover:text-destructive hover:bg-destructive/10" : "text-muted-foreground hover:text-destructive hover:bg-destructive/10"}`}
          >
            <Trash2 className={`h-4 w-4 ${isDeleting ? "animate-pulse" : ""}`} />
          </Button>
        </div>
      </div>

      <div className="flex items-center justify-between">
        <button
          onClick={copyStudentLink}
          className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-sidebar rounded-md text-[12px] font-mono font-medium text-sidebar-foreground hover:bg-sidebar/80 transition-colors"
        >
          {copied ? (
            <>
              <Check className="h-3 w-3 text-green-600" />
              Link copied
            </>
          ) : (
            <>
              <Copy className="h-3 w-3" />
              {lesson.code}
            </>
          )}
        </button>
        <span className="text-[12px] text-muted-foreground">
          {formatDate(new Date(lesson.created_at).toISOString())}
        </span>
      </div>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-12 text-center shadow-sm">
      <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-sidebar mb-4">
        <BookOpen className="h-8 w-8 text-sidebar-foreground" />
      </div>
      <h3 className="text-lg font-semibold text-foreground mb-2">
        No pre lessons yet
      </h3>
      <p className="text-muted-foreground text-[15px] mb-6 max-w-md mx-auto">
        Create your first pre lesson to help students prepare for class with
        materials, readings, and AI-powered quizzes.
      </p>
      <Link to="/create-pre-lesson">
        <Button
          variant="outline"
          className="border-primary text-primary hover:bg-background font-semibold gap-2"
        >
          <Plus className="h-4 w-4" />
          Get Started
        </Button>
      </Link>
    </div>
  );
}

function Home() {
  const { lessons, isLoading, deleteLesson } = usePreLessonStore();
  const [deletingId, setDeletingId] = useState<number | null>(null);

  const handleDelete = async (id: number) => {
    setDeletingId(id);
    try {
      await ApiClient.delete(`/pre-lesson/${id}`);
      deleteLesson(id);
    } catch (err) {
      console.error("Failed to delete:", err);
    } finally {
      setDeletingId(null);
    }
  };

  if (isLoading) {
    return (
      <div className="max-w-5xl mx-auto">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-48 mb-8" />
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-32 bg-gray-100 rounded-lg" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-[28px] font-bold text-foreground">Pre Lessons</h1>
        <Link to="/create-pre-lesson">
          <Button className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold gap-2 h-10 px-5">
            <Plus className="h-4 w-4" />
            Create Pre Lesson
          </Button>
        </Link>
      </div>

      {lessons.length === 0 ? (
        <EmptyState />
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {lessons.map((lesson) => (
            <PreLessonCard
              key={lesson.id}
              lesson={lesson}
              onDelete={handleDelete}
              isDeleting={deletingId === lesson.id}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default Home;
